import { WILDCARD } from '../../constants/permissions_constants';

/**
 * This method pulls the region ID out of the locale string. That string
 * should have the format region_id!building_id!area_id!room_id
 *
 * @param localeId
 * @returns {*|null}
 */
export function getRegionId(localeId) {
    if (localeId === undefined) {
        return null;
    }

    if (localeId.trim() === "") {
        return null;
    }

    // Split this ID
    const localeIdArray = localeId.split('!');

    // Return the first element. This is the region's ID
    return localeIdArray[0];
}

/**
 * This method pulls the building ID out of the locale string. That string
 * should have the format region_id!building_id!area_id!room_id
 *
 * @param localeId
 * @returns {*|null}
 */
export function getBuildingId(localeId) {
    if (localeId === undefined) {
        return null;
    }

    if (localeId.trim() === "") {
        return null;
    }

    // Split this ID
    const localeIdArray = localeId.split('!');

    // If this ID doesn't have at least 2 parts, return null
    if (localeIdArray.length < 2) {
        return null
    }

    // Return the second element. This is the building's ID
    return localeIdArray[1];
}

/**
 * This method pulls the building ID out of the locale string. That string
 * should have the format region_id!building_id!area_id!room_id
 *
 * @param localeId
 * @returns {*|null}
 */
export function getAreaId(localeId) {
    if (localeId === undefined) {
        return null;
    }

    if (localeId.trim() === "") {
        return null;
    }

    // Split this ID
    const localeIdArray = localeId.split('!');

    // If this ID doesn't have at least 3 parts, return null
    if (localeIdArray.length < 3) {
        return null
    }

    // Return the third element. This is the area's ID
    return localeIdArray[2];
}

/**
 * This method pulls the room ID out of the locale string. That string
 * should have the format region_id!building_id!area_id!room_id
 *
 * @param localeId
 * @returns {*|null}
 */
export function getRoomId(localeId) {
    if (localeId === undefined) {
        return null;
    }

    if (localeId.trim() === "") {
        return null;
    }

    // Split this ID
    const localeIdArray = localeId.split('!');

    // If this ID doesn't have at least 4 parts, return null
    if (localeIdArray.length < 4) {
        return null
    }

    // Return the third element. This is the area's ID
    return localeIdArray[3];
}

/**
 * Resolve a locale path (e.g. "region-id!building-id!area-id") to a human-readable
 * display string using the allLocalesStore hierarchy data.
 *
 * @param {string} localePath - The !-delimited locale path
 * @param {Object} localesStoreData - The allLocalesStore value (contains .locales.regions)
 * @returns {string} Human-readable name (e.g. "North Campus → Science Building (All)")
 *                   or the raw path if resolution fails
 */
export function resolveLocalePath(localePath, localesStoreData) {
    if (localePath === '*') return 'All Facilities'

    if (!localesStoreData || !localesStoreData.locales || !localesStoreData.locales.regions) {
        return localePath
    }

    const parts = localePath.split('!')
    const regions = localesStoreData.locales.regions
    const names = []

    // Resolve region
    const region = regions.find(r => r.id === parts[0])
    if (!region) return localePath
    names.push(region.name)

    if (parts.length === 1) return names.join(' → ')
    if (parts[1] === '*') return `${ names.join(' → ') } (All)`

    // Resolve building
    const buildings = region.buildings || []
    const building = buildings.find(b => b.id === parts[1])
    if (!building) return localePath
    names.push(building.name)

    if (parts.length === 2) return names.join(' → ')
    if (parts[2] === '*') return `${ names.join(' → ') } (All)`

    // Resolve area
    const areas = building.areas || []
    const area = areas.find(a => a.id === parts[2])
    if (!area) return localePath
    names.push(area.name)

    if (parts.length === 3) return names.join(' → ')
    if (parts[3] === '*') return `${ names.join(' → ') } (All)`

    // Resolve room
    const rooms = area.rooms || []
    const room = rooms.find(r => r.id === parts[3])
    if (!room) return localePath
    names.push(room.name)

    return names.join(' → ')
}

/**
 * Remove the trailing "!*" wildcard suffix from a locale path if present.
 *
 * Mirrors remove_locale_wildcard_suffix in app/services/mixins/locale_accessibility.rb:
 * a granted path like "region-id!*" is access-equivalent to its covering path
 * "region-id", so stripping the suffix lets exact-match consumers (e.g. the facility
 * checkbox tree) recognize legacy wildcard-suffixed grants. Only the "!*" SUFFIX is
 * stripped — a BARE "*" (the global wildcard) is returned unchanged.
 *
 * @param {string} localePath - The !-delimited locale path (may carry a trailing "!*")
 * @returns {string} The path with any trailing "!*" removed, or the input unchanged
 */
export function removeLocaleWildcardSuffix(localePath) {
    if (typeof localePath !== 'string') {
        return localePath;
    }

    const suffix = `!${ WILDCARD }`;
    if (localePath.endsWith(suffix)) {
        return localePath.slice(0, -suffix.length);
    }

    return localePath;
}
